import { Icon } from '@iconify/react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { capitalize } from '@mui/material/utils';
import { useEffect, useRef } from 'react';
import { normalizeRole } from '../auth/session';
import { useAuth } from '../modules/auth/auth.context';
import NotificationBell from '../modules/tasks/components/NotificationBell';
import HelpButton from '../help/HelpButton';
import { useAccess } from '../auth/access';

export default function Header() {
  const { user, role: userRole } = useAuth();
  const access = useAccess();
  const role = normalizeRole(userRole);

  const headerRef = useRef(null);

  useEffect(() => {
    if (!headerRef.current) return;

    const observer = new ResizeObserver(([entry]) => {
      const headerHeight = entry.contentRect.height + 40; // top and bottom padding
      document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
    });

    observer.observe(headerRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <AppBar
      elevation={0}
      sx={{
        backgroundColor: 'primary.contrastText',
        color: 'primary.main',
        width: 'calc(100% - var(--sidebar-width))',
        marginLeft: 'var(--sidebar-width)',
      }}
    >
      <Toolbar
        ref={headerRef}
        disableGutters
        // Note: if adding more than 1 items - change to justifyContent='space-between'
        sx={{ justifyContent: 'flex-end', padding: '24px 40px 16px' }}
      >
        <Stack direction='row' alignItems='center' gap={2}>
          <HelpButton />
          <NotificationBell />
          {user?.photo ? (
            <Box
              sx={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                border: '1px solid ',
                borderColor: 'grey.300',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <img
                src={user?.photo as string}
                alt='User Profile'
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
              />
            </Box>
          ) : (
            <Icon icon='fluent-emoji:man-light' width={40} />
          )}

          <Stack>
            <Typography fontSize='1.125rem' fontWeight={500} color='text.primary'>
              {user?.name}
            </Typography>
            <Typography variant='body1' color='text.primary'>
              {access?.roleLabel ?? capitalize(String(role || 'user'))}
            </Typography>
          </Stack>
        </Stack>
      </Toolbar>
    </AppBar>
  );
}
